import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { EntityService } from '@app/core/services';
import { Observable } from 'rxjs';

/**
 * Interface for direct supplier search request payload
 */
export interface DirectSearchRequest {
    entityId: string;
    itemName: string;
}

/**
 * Interface for supplier product in direct search response
 */
export interface DirectSearchProduct {
    item_name: string;
    sku_id: string;
    rate: number;
    mrp?: number;
    unit: string;
    quantity: number;
    image_url?: string;
    product_images?: string;
    platform: string;
    is_in_stock?: boolean;
    bulkPrices?: { qty: number; price: number; type?: string }[];
    supplierName?: string;
    supplierId?: string;
    category_id?: string;
    vertical?: string;
    minimumOrderQty?: number;
    maximumOrderQty?: number;
    avlOrderQty?: number;
    product_number?: string;
    isBestMatchPerSupplier?: boolean;
    isBestOverallMatch?: boolean;
}

/**
 * Interface for supplier-specific search results
 */
export interface SupplierSearchResult {
    bestMatch: DirectSearchProduct | null;
    similarItems: DirectSearchProduct[];
}

/**
 * Interface for direct search response
 */
export interface DirectSearchResponse {
    success: boolean;
    hyperpure?: SupplierSearchResult;
    udaan?: SupplierSearchResult;
    dmart?: SupplierSearchResult;
    supplierWithRateCard?: { [key: string]: SupplierSearchResult };
    bestMatchPerSupplier?: { [platform: string]: DirectSearchProduct };
    bestOverallMatch?: DirectSearchProduct;
}

/**
 * Service for direct supplier search that bypasses the GCP layer.
 * This service makes direct API calls to the dashboard microservice
 * which handles supplier integrations directly.
 */
@Injectable({
    providedIn: 'root'
})
export class DirectPriceDiscoveryService {

    constructor(
        private http: HttpClient,
        private entityService: EntityService,
        private appGlobals: AppGlobals
    ) { }

    /**
     * Creates HTTP headers with entity ID
     */
    private getHeaders(entityId: string): HttpHeaders {
        let id = entityId;

        if (!id) {
            const currentEntity = this.entityService.getSingleSelectedEntity();
            id = currentEntity?.id || '';
        }

        return new HttpHeaders({
            'x-entity-ids': id || ''
        });
    }

    /**
     * Direct supplier search API call.
     * Bypasses GCP layer and calls dashboard microservice directly.
     * Backend searches all integrated suppliers (Hyperpure, Udaan, Dmart, Local)
     * and returns consolidated results with best match calculations.
     *
     * POST /pm/search-direct
     *
     * @param payload DirectSearchRequest containing entityId and itemName
     * @returns Observable<DirectSearchResponse> with supplier results and best matches
     */
    searchDirect(payload: DirectSearchRequest): Observable<DirectSearchResponse> {
        const headers = this.getHeaders(payload.entityId);

        // Transform payload to match backend API expectations
        const requestBody = {
            search_text: payload.itemName,
            company_id: payload.entityId,
            enabled_suppliers: ['hyperpure', 'udaan', 'dmart', 'local']
        };

        return this.http.post<DirectSearchResponse>(
            `${this.appGlobals.apiUrl.dashboard}pm/search-direct`,
            requestBody,
            { headers }
        );
    }

    /**
     * Search multiple items directly.
     * Useful for batch searching.
     *
     * POST /pm/search-direct-batch
     *
     * @param entityId Entity ID
     * @param itemNames Array of item names to search
     * @returns Observable with results for each item
     */
    searchDirectBatch(entityId: string, itemNames: string[]): Observable<{ [itemName: string]: DirectSearchResponse }> {
        const headers = this.getHeaders(entityId);

        // Transform to backend API format
        const requestBody = {
            company_id: entityId,
            items: itemNames.map(name => ({ search_text: name })),
            enabled_suppliers: ['hyperpure', 'udaan', 'dmart', 'local']
        };

        return this.http.post<{ [itemName: string]: DirectSearchResponse }>(
            `${this.appGlobals.apiUrl.dashboard}pm/search-direct-batch`,
            requestBody,
            { headers }
        );
    }

    /**
     * Check integration status for all suppliers.
     * Same as price-discovery.service but kept here to avoid dependency.
     */
    checkIntegrationStatus(entityId: string): Observable<any> {
        const headers = this.getHeaders(entityId);
        return this.http.get<any>(
            `${this.appGlobals.apiUrl.authentication}check-integration-status/${entityId}`,
            { headers }
        );
    }

    /**
     * Get suppliers list for the entity.
     */
    getSuppliers(entityId: string, dropdown: boolean = true): Observable<any[]> {
        const headers = this.getHeaders(entityId);
        const params: any = {};
        if (dropdown) params.dropdown = 'true';
        return this.http.get<any[]>(`${this.appGlobals.apiUrl.dashboard}supplier-list`, {
            headers,
            params
        });
    }

    /**
     * Get recent purchase items for quick add functionality.
     */
    getRecentPurchaseItems(params: any, entityId: string): Observable<any[]> {
        const headers = this.getHeaders(entityId);
        return this.http.post<any[]>(
            `${this.appGlobals.apiUrl.dashboard}last-20-purchase-items`,
            params,
            { headers }
        );
    }

    /**
     * Get most purchased items for quick add functionality.
     */
    getMostPurchasedItems(params: any, entityId: string): Observable<any[]> {
        const headers = this.getHeaders(entityId);
        return this.http.post<any[]>(
            `${this.appGlobals.apiUrl.dashboard}most-purchased-items`,
            params,
            { headers }
        );
    }

    /**
     * Get most searched items for quick add functionality.
     */
    getMostSearchedItems(params: any, entityId: string): Observable<any[]> {
        const headers = this.getHeaders(entityId);
        return this.http.post<any[]>(
            `${this.appGlobals.apiUrl.dashboard}most-searched-items`,
            params,
            { headers }
        );
    }

    /**
     * Get last searched items for quick add functionality.
     */
    getLastSearchedItems(params: any, entityId: string): Observable<any[]> {
        const headers = this.getHeaders(entityId);
        return this.http.post<any[]>(
            `${this.appGlobals.apiUrl.dashboard}last-searched-items`,
            params,
            { headers }
        );
    }

    /**
     * Get top ordered items for quick add functionality.
     */
    getTopOrderedItems(params: any, entityId: string): Observable<any[]> {
        const headers = this.getHeaders(entityId);
        return this.http.post<any[]>(
            `${this.appGlobals.apiUrl.dashboard}top-ordered-items`,
            params,
            { headers }
        );
    }

    // ─────────────────────────────────────────────────────────────
    // Cart Management APIs (reused from price-discovery.service)
    // ─────────────────────────────────────────────────────────────

    /**
     * Add item to Hyperpure cart
     */
    addToHyperpureCart(productId: number, quantity: number, deliveryMode: string, entityId: string): Observable<any> {
        const headers = this.getHeaders(entityId);
        return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
            product_id: productId,
            quantity,
            delivery_mode: deliveryMode
        }, { headers });
    }

    /**
     * Add item to DMart cart
     */
    addToDmartCart(productId: string, quantity: number, entityId: string): Observable<any> {
        const headers = this.getHeaders(entityId);
        const payload = {
            orderItem: [{
                productId: productId,
                quantity: String(quantity)
            }]
        };
        return this.http.post<any>(
            `${this.appGlobals.apiUrl.dashboard}dmart/add-to-cart`,
            payload,
            { headers }
        );
    }

    /**
     * Update Udaan cart
     */
    updateUdaanCart(
        productId: string,
        qty: number,
        deliveryMode: string,
        storeUuid: string,
        categoryId: string | undefined,
        entityId: string,
        vertical: string,
        skipRedirect: boolean = false
    ): Observable<any> {
        const headers = this.getHeaders(entityId);
        const body = {
            product_id: productId,
            qty: qty,
            delivery_mode: deliveryMode,
            store_uuid: storeUuid,
            category_id: categoryId,
            vertical: vertical
        };
        return this.http.post<any>(
            `${this.appGlobals.apiUrl.authentication}udaan/cart/update`,
            body,
            { headers }
        );
    }

    // ─────────────────────────────────────────────────────────────
    // Favourite Products APIs (reused from price-discovery.service)
    // ─────────────────────────────────────────────────────────────

    /**
     * Get all favourite products for a company
     */
    getFavouriteProducts(companyId: string): Observable<any> {
        const headers = this.getHeaders(companyId);
        return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites`, {
            headers,
            params: { companyId }
        });
    }

    /**
     * Add a product to favourites
     */
    addFavouriteProduct(payload: any): Observable<any> {
        const headers = this.getHeaders(payload.companyId);
        return this.http.post<any>(
            `${this.appGlobals.apiUrl.dashboard}pm/favourites`,
            payload,
            { headers }
        );
    }

    /**
     * Remove a product from favourites
     */
    removeFavouriteProduct(payload: any): Observable<any> {
        const headers = this.getHeaders(payload.companyId);
        return this.http.request<any>('delete', `${this.appGlobals.apiUrl.dashboard}pm/favourites`, {
            headers,
            body: payload
        });
    }

    /**
     * Search favourite items
     */
    searchFavouriteItems(entityId: string): Observable<any> {
        const headers = this.getHeaders(entityId);
        return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites/search`, {
            headers,
            params: { companyId: entityId }
        });
    }
}
